import { useState, useEffect } from 'react';
import { 
  Button, 
  Dialog,
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Switch,
  Typography
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { COLOR_PURPLE_0 } from "../../../app/ui/colorConstants";

function InvoiceColumnManager({ 
  columns = [], 
  onUpdateColumns 
}) {
  const [open, setOpen] = useState(false);
  const [columnState, setColumnState] = useState([]);

  useEffect(() => {
    // Initialize column state when columns prop changes
    if (columns && columns.length > 0) {
      setColumnState(columns.map(col => ({
        ...col,
        visible: col.visible !== false
      })));
    }
  }, [columns]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(columnState);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setColumnState(items);
  };

  const handleToggleVisible = (field, visible) => {
    setColumnState(prevState => 
      prevState.map(col => 
        col.field === field ? { ...col, visible } : col
      )
    );
  };

  const handleSave = () => {
    onUpdateColumns(columnState);
    setOpen(false);
  };

  const handleReset = () => {
    // Reset to original column order and visibility
    setColumnState(columns.map(col => ({
      ...col,
      visible: true
    })));
  };

  return (
    <>
      <Button 
        onClick={() => setOpen(true)}
        style={{ color: COLOR_PURPLE_0, marginRight: 8 }}
      >
        Manage Columns
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle>Manage Columns</DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" gutterBottom>
            Drag to reorder columns. Toggle visibility with the switches.
          </Typography>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ 
                    height: '100%',
                    flex: 1, 
                    overflowY: 'auto',
                    paddingRight: 8
                  }}
                >
                  {columnState.map((column, index) => (
                    <Draggable 
                      key={column.field?.toString() || index.toString()} 
                      draggableId={column.field?.toString() || index.toString()} 
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            userSelect: 'none',
                            padding: 4,
                            paddingLeft: 16,
                            margin: '0 0 8px 0',
                            backgroundColor: '#f9f9f9',
                            border: '1px solid #f0f0f0',
                            borderRadius: 4,
                            ...provided.draggableProps.style,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <span>{column.headerName || column.field}</span>
                          <Switch
                            checked={column.visible !== false}
                            onChange={(e) => handleToggleVisible(column.field, e.target.checked)}
                            disabled={column.field === 'invoiceNumber'} // Don't allow hiding the invoice number column
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReset}>Reset</Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            style={{ backgroundColor: COLOR_PURPLE_0 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default InvoiceColumnManager; 